import React, { useRef, useState, useCallback, useEffect } from "react";
import api from "../utils/axiosConfig";
import localforage from "localforage";
import "../css/micDictation.css";

const API_URL = process.env.REACT_APP_API_URL;

// Recording support (mic capture + MediaRecorder). Works in Chrome, Edge,
// Firefox and modern mobile browsers — no longer tied to Web Speech API.
const RECORDING_SUPPORTED =
  typeof navigator !== "undefined" &&
  !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia) &&
  typeof window !== "undefined" &&
  typeof window.MediaRecorder !== "undefined";

// Pick an audio container the browser can actually produce.
function pickMimeType() {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/mp4",
  ];
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) {
    return "";
  }
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || "";
}

// Outline microphone icon
const MicIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="17"
    height="17"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="9" y="2" width="6" height="11" rx="3" />
    <path d="M5 10v1a7 7 0 0 0 14 0v-1" />
    <line x1="12" y1="19" x2="12" y2="22" />
    <line x1="8" y1="22" x2="16" y2="22" />
  </svg>
);

const fmt = (s) => {
  const m = Math.floor(s / 60);
  const ss = String(s % 60).padStart(2, "0");
  return `${m}:${ss}`;
};

/**
 * MicDictation — records audio, transcribes with Groq Whisper Large v3, then
 * AI-cleans/summarises via /api/ai/clean-text.
 *
 * Props (unchanged):
 *  - lang: BCP-47 (e.g. "en-IN", "pa-IN") — sent to Whisper as a hint
 *  - split: if true, onResult({ short, long }); else onResult({ short:"", long })
 *  - context: backend hint ("discussion" | "stc_actionable" | "leader_actionable" | "remarks")
 *  - onResult({ short, long })
 *  - label: tooltip / aria-label
 */
export default function MicDictation({
  lang = "en-IN",
  split = false,
  context = "",
  onResult,
  label = "Dictate",
}) {
  const [recording, setRecording] = useState(false);
  // phase: "" | "transcribing" | "summarising"
  const [phase, setPhase] = useState("");
  const [elapsed, setElapsed] = useState(0);

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const mimeRef = useRef("");
  const timerRef = useRef(null);

  const processing = phase !== "";
  const supported = RECORDING_SUPPORTED;

  useEffect(() => {
    // Cleanup on unmount: stop any live tracks/timer.
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  // Send raw transcript through the existing AI cleaner/summariser.
  const summarise = useCallback(
    async (rawText) => {
      const text = (rawText || "").trim();
      if (!text) {
        setPhase("");
        return;
      }
      setPhase("summarising");
      try {
        const token = await localforage.getItem("token");
        const { data } = await api.post(
          `${API_URL}/ai/clean-text`,
          { text, mode: split ? "split" : "summary", context },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (split) {
          onResult &&
            onResult({
              short: (data && data.point) || "",
              long: (data && data.details) || text,
            });
        } else {
          onResult && onResult({ short: "", long: (data && data.text) || text });
        }
      } catch (e) {
        // graceful fallback — insert the raw transcript
        onResult && onResult({ short: "", long: text });
      } finally {
        setPhase("");
      }
    },
    [split, context, onResult]
  );

  // Upload the recorded audio to Groq Whisper, then summarise.
  const transcribe = useCallback(
    async (blob) => {
      if (!blob || !blob.size) {
        setPhase("");
        return;
      }
      setPhase("transcribing");
      try {
        const token = await localforage.getItem("token");
        const ext = (mimeRef.current || "").includes("mp4") ? "mp4" : "webm";
        const fd = new FormData();
        fd.append("audio", blob, `dictation.${ext}`);
        fd.append("lang", lang || "");

        const { data } = await api.post(`${API_URL}/ai/transcribe`, fd, {
          headers: { Authorization: `Bearer ${token}` },
        });
        await summarise((data && data.text) || "");
      } catch (e) {
        setPhase("");
        const msg =
          (e && e.response && e.response.data && e.response.data.error) ||
          "Could not transcribe the audio. Please try again.";
        alert(msg);
      }
    },
    [lang, summarise]
  );

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    const rec = recorderRef.current;
    if (rec && rec.state !== "inactive") {
      try {
        rec.stop(); // fires onstop -> transcribe
      } catch (e) {
        /* no-op */
      }
    }
    setRecording(false);
  }, []);

  const start = useCallback(async () => {
    if (!supported) {
      alert(
        "Voice input isn't supported in this browser. Please use Google Chrome, Microsoft Edge, or Firefox."
      );
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mime = pickMimeType();
      mimeRef.current = mime;
      const rec = mime
        ? new MediaRecorder(stream, { mimeType: mime })
        : new MediaRecorder(stream);
      recorderRef.current = rec;

      rec.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
      };
      rec.onstop = () => {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        const type = mimeRef.current || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        chunksRef.current = [];
        transcribe(blob);
      };

      rec.start();
      setElapsed(0);
      setRecording(true);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (e) {
      if (e && (e.name === "NotAllowedError" || e.name === "SecurityError")) {
        alert("Microphone access was blocked. Please allow the mic and try again.");
      } else {
        alert("Could not start recording. Please check your microphone.");
      }
      setRecording(false);
    }
  }, [supported, transcribe]);

  return (
    <div className="mic-wrap">
      <button
        type="button"
        className={`mic-btn${recording ? " listening" : ""}`}
        onClick={recording ? stop : start}
        disabled={processing}
        title={
          phase === "transcribing"
            ? "Transcribing…"
            : phase === "summarising"
            ? "Summarising…"
            : recording
            ? "Stop & Insert"
            : label
        }
        aria-label={label}
      >
        {processing ? <span className="mic-spinner" /> : <MicIcon />}
      </button>

      {recording && (
        <div className="mic-live" role="status">
          <div className="mic-live-head">
            <span className="mic-dot" /> Recording… {fmt(elapsed)} — speak freely,
            pauses are fine
          </div>
          <button type="button" className="mic-stop" onClick={stop}>
            Stop &amp; Insert
          </button>
        </div>
      )}
      {phase === "transcribing" && (
        <div className="mic-live mic-proc">Transcribing…</div>
      )}
      {phase === "summarising" && (
        <div className="mic-live mic-proc">Summarising…</div>
      )}
    </div>
  );
}
